const numeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const roundPosition = value => {
  const rounded = Math.round(value * 1000) / 1000;
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const isMovableObject = object => Boolean(
  object &&
  object.isBackground !== true &&
  object.draggable !== false &&
  object.locked !== true &&
  object.isLocked !== true
);

const localBounds = object => {
  const points = Array.isArray(object.points) ? object.points : [];
  if (points.length >= 2) {
    const xs = points.filter((_, index) => index % 2 === 0).map(value => numeric(value));
    const ys = points.filter((_, index) => index % 2 === 1).map(value => numeric(value));
    return {
      minX: Math.min(...xs),
      minY: Math.min(...ys),
      maxX: Math.max(...xs),
      maxY: Math.max(...ys)
    };
  }

  const radiusX = numeric(object.radiusX, numeric(object.radius));
  const radiusY = numeric(object.radiusY, numeric(object.radius));
  if (radiusX > 0 || radiusY > 0) {
    return { minX: -radiusX, minY: -radiusY, maxX: radiusX, maxY: radiusY };
  }

  return {
    minX: 0,
    minY: 0,
    maxX: Math.max(0, numeric(object.width)),
    maxY: Math.max(0, numeric(object.height))
  };
};

// Persisted card objects have flattened scale, so their rectangle/points plus
// rotation reproduce the visible axis-aligned Konva bounds without a Stage read.
export const getTransformedBounds = object => {
  const bounds = localBounds(object);
  const stroke = Math.max(0, numeric(object.strokeWidth)) / 2;
  const corners = [
    [bounds.minX - stroke, bounds.minY - stroke],
    [bounds.maxX + stroke, bounds.minY - stroke],
    [bounds.maxX + stroke, bounds.maxY + stroke],
    [bounds.minX - stroke, bounds.maxY + stroke]
  ];
  const radians = numeric(object.rotation) * Math.PI / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const originX = numeric(object.x);
  const originY = numeric(object.y);
  const transformed = corners.map(([x, y]) => ({
    x: originX + x * cosine - y * sine,
    y: originY + x * sine + y * cosine
  }));
  const xs = transformed.map(point => point.x);
  const ys = transformed.map(point => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
};

const selectedMovable = (objects, selectedIds) => {
  const selected = new Set(selectedIds || []);
  return objects
    .filter(object => selected.has(object.id) && isMovableObject(object))
    .map(object => ({ object, bounds: getTransformedBounds(object) }));
};

export const alignObjects = (objects, selectedIds, alignment) => {
  const selected = selectedMovable(objects, selectedIds);
  if (selected.length < 2) return objects;

  const selectionMinX = Math.min(...selected.map(item => item.bounds.minX));
  const selectionMaxX = Math.max(...selected.map(item => item.bounds.maxX));
  const selectionCenterX = (selectionMinX + selectionMaxX) / 2;
  const selectionMinY = Math.min(...selected.map(item => item.bounds.minY));
  const selectionMaxY = Math.max(...selected.map(item => item.bounds.maxY));
  const selectionCenterY = (selectionMinY + selectionMaxY) / 2;
  const vertical = alignment === 'top' || alignment === 'middle' || alignment === 'bottom';
  const positionKey = vertical ? 'y' : 'x';
  const deltas = new Map(selected.map(item => {
    let delta = 0;
    if (alignment === 'left') delta = selectionMinX - item.bounds.minX;
    if (alignment === 'center') delta = selectionCenterX - item.bounds.centerX;
    if (alignment === 'right') delta = selectionMaxX - item.bounds.maxX;
    if (alignment === 'top') delta = selectionMinY - item.bounds.minY;
    if (alignment === 'middle') delta = selectionCenterY - item.bounds.centerY;
    if (alignment === 'bottom') delta = selectionMaxY - item.bounds.maxY;
    return [item.object.id, delta];
  }));

  return objects.map(object => deltas.has(object.id)
    ? {
        ...object,
        [positionKey]: roundPosition(numeric(object[positionKey]) + deltas.get(object.id))
      }
    : object);
};

const scaleNumber = (value, factor) => Number.isFinite(Number(value))
  ? roundPosition(Number(value) * factor)
  : value;

const scalePoints = (points, factor) => Array.isArray(points)
  ? points.map(value => scaleNumber(value, factor))
  : points;

const scaleObjectGeometry = (object, factor) => {
  const next = { ...object };
  if (Number.isFinite(Number(object.width))) next.width = scaleNumber(object.width, factor);
  if (Number.isFinite(Number(object.height))) next.height = scaleNumber(object.height, factor);
  if (Array.isArray(object.points)) next.points = scalePoints(object.points, factor);
  if (Number.isFinite(Number(object.radius))) next.radius = scaleNumber(object.radius, factor);
  if (Number.isFinite(Number(object.radiusX))) next.radiusX = scaleNumber(object.radiusX, factor);
  if (Number.isFinite(Number(object.radiusY))) next.radiusY = scaleNumber(object.radiusY, factor);
  if (object.meta && typeof object.meta === 'object') {
    next.meta = { ...object.meta };
    if (Number.isFinite(Number(object.meta.radiusX))) {
      next.meta.radiusX = scaleNumber(object.meta.radiusX, factor);
    }
    if (Number.isFinite(Number(object.meta.radiusY))) {
      next.meta.radiusY = scaleNumber(object.meta.radiusY, factor);
    }
  }
  if (object.type === 'text') {
    next.fontSize = scaleNumber(numeric(object.fontSize, 48), factor);
    if (Number.isFinite(Number(object.padding))) next.padding = scaleNumber(object.padding, factor);
    if (Number.isFinite(Number(object.letterSpacing))) {
      next.letterSpacing = scaleNumber(object.letterSpacing, factor);
    }
  }
  return next;
};

const minimumSafeScale = object => {
  if (object.type === 'text') {
    const fontSize = Math.max(1, numeric(object.fontSize, 48));
    const minimumFontSize = Math.max(1, numeric(object.minimumFontSize, 8));
    return Math.min(1, minimumFontSize / fontSize);
  }
  if (object.type === 'qrcode') {
    const bounds = localBounds(object);
    const shortestSide = Math.max(1, Math.min(
      bounds.maxX - bounds.minX,
      bounds.maxY - bounds.minY
    ));
    const minimumQrSize = Math.max(1, numeric(object.minimumQrSize, 80));
    return Math.min(1, minimumQrSize / shortestSide);
  }
  return 0.001;
};

const translateInside = (object, cardWidth, cardHeight) => {
  const bounds = getTransformedBounds(object);
  let dx = 0;
  let dy = 0;

  if (bounds.width > cardWidth) dx = cardWidth / 2 - bounds.centerX;
  else if (bounds.minX < 0) dx = -bounds.minX;
  else if (bounds.maxX > cardWidth) dx = cardWidth - bounds.maxX;

  if (bounds.height > cardHeight) dy = cardHeight / 2 - bounds.centerY;
  else if (bounds.minY < 0) dy = -bounds.minY;
  else if (bounds.maxY > cardHeight) dy = cardHeight - bounds.maxY;

  if (Math.abs(dx) < 0.0005 && Math.abs(dy) < 0.0005) return object;
  return {
    ...object,
    x: roundPosition(numeric(object.x) + dx),
    y: roundPosition(numeric(object.y) + dy)
  };
};

const fitsCard = (object, cardWidth, cardHeight) => {
  const bounds = getTransformedBounds(object);
  return bounds.width <= cardWidth + 0.001 && bounds.height <= cardHeight + 0.001;
};

const maximumFittingScale = (object, cardWidth, cardHeight) => {
  let low = 0;
  let high = 1;
  for (let index = 0; index < 32; index += 1) {
    const candidate = (low + high) / 2;
    if (fitsCard(scaleObjectGeometry(object, candidate), cardWidth, cardHeight)) low = candidate;
    else high = candidate;
  }
  return low;
};

// Repositions only objects that are outside the new card. Oversize objects are
// proportionally reduced only when their text/QR legibility floor is respected.
export const fitObjectsWithinCard = (objects, cardWidth, cardHeight) => {
  if (!Number.isFinite(cardWidth) || !Number.isFinite(cardHeight) || cardWidth <= 0 || cardHeight <= 0) {
    return { objects, warnings: [], changed: false };
  }

  const warnings = [];
  const nextObjects = objects.map(object => {
    if (!object || object.isBackground === true) return object;
    const before = getTransformedBounds(object);
    const alreadyVisible = before.minX >= 0 && before.minY >= 0 &&
      before.maxX <= cardWidth && before.maxY <= cardHeight;
    if (alreadyVisible) return object;

    let next = object;
    if (before.width > cardWidth || before.height > cardHeight) {
      const factor = maximumFittingScale(object, cardWidth, cardHeight);
      const safeMinimum = minimumSafeScale(object);
      if (factor >= safeMinimum && factor > 0) {
        next = scaleObjectGeometry(object, factor);
      } else {
        warnings.push({
          objectId: object.id,
          objectType: object.type,
          reason: object.type === 'text'
            ? 'minimum text size would be exceeded'
            : object.type === 'qrcode'
              ? 'minimum QR size would be exceeded'
              : 'object cannot be resized safely'
        });
      }
    }

    return translateInside(next, cardWidth, cardHeight);
  });

  return {
    objects: nextObjects,
    warnings,
    changed: nextObjects.some((object, index) => object !== objects[index])
  };
};

export const distributeObjects = (objects, selectedIds, axis) => {
  const selected = selectedMovable(objects, selectedIds);
  if (selected.length < 3) return objects;

  const horizontal = axis === 'horizontal';
  const minKey = horizontal ? 'minX' : 'minY';
  const maxKey = horizontal ? 'maxX' : 'maxY';
  const sizeKey = horizontal ? 'width' : 'height';
  const positionKey = horizontal ? 'x' : 'y';
  const ordered = [...selected].sort((a, b) => a.bounds[minKey] - b.bounds[minKey]);
  const span = ordered[ordered.length - 1].bounds[maxKey] - ordered[0].bounds[minKey];
  const occupied = ordered.reduce((sum, item) => sum + item.bounds[sizeKey], 0);
  const gap = (span - occupied) / (ordered.length - 1);
  const deltas = new Map();
  let cursor = ordered[0].bounds[minKey];

  ordered.forEach((item, index) => {
    deltas.set(
      item.object.id,
      index === 0 || index === ordered.length - 1 ? 0 : cursor - item.bounds[minKey]
    );
    cursor += item.bounds[sizeKey] + gap;
  });

  return objects.map(object => deltas.has(object.id)
    ? {
        ...object,
        [positionKey]: roundPosition(numeric(object[positionKey]) + deltas.get(object.id))
      }
    : object);
};
